import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { api, type LayoutResponse, type ReservationRow } from '../lib/api.js';
import { useVenueId } from '../lib/session.js';
import { useVenue } from '../hooks/useVenue.js';
import { currentServiceDate, formatTime, minutesToClock, venueClock, clockToMinutes } from '../lib/time.js';
import { inlineStartPercent, useDirection } from '../lib/direction.js';
import { ServiceDatePicker } from '../components/ServiceDatePicker.js';
import { StatusPill } from '../components/StatusPill.js';

/**
 * The timeline: tables as rows, time as columns.
 *
 * The axis RUNS RIGHT TO LEFT IN ARABIC. This is the second place logical CSS
 * cannot help — a booking positioned at 40% along a track sits in the same
 * physical spot whatever `dir` says, so an Arabic reader would find the evening
 * running backwards against every other direction on the screen.
 *
 * `inlineStartPercent` does the flip. Everything downstream is ordinary layout.
 *
 * The window runs 06:00 to 30:00 rather than midnight to midnight, so a suhoor
 * booking at 01:30 appears at the END of the evening it belongs to instead of
 * at the start of a day it has nothing to do with.
 */

const WINDOW_START_MINUTES = 6 * 60;
const WINDOW_END_MINUTES = 30 * 60;
const WINDOW_SPAN = WINDOW_END_MINUTES - WINDOW_START_MINUTES;

export function TimelineScreen() {
  const { t, i18n } = useTranslation();
  const venueId = useVenueId();
  const venue = useVenue();
  const dir = useDirection();

  const [serviceDate, setServiceDate] = useState(() =>
    venue ? currentServiceDate(venue) : '',
  );
  const date = serviceDate || (venue ? currentServiceDate(venue) : '');

  const layout = useQuery({
    queryKey: ['layout', venueId],
    enabled: Boolean(venueId),
    queryFn: () => api.get<LayoutResponse>('/api/v1/floor/layout', { venue_id: venueId }),
    staleTime: 5 * 60_000,
  });

  const bookings = useQuery({
    queryKey: ['reservations', venueId, date],
    enabled: Boolean(venueId && date),
    queryFn: () =>
      api.get<{ reservations: ReservationRow[] }>('/api/v1/reservations', {
        venue_id: venueId,
        date,
      }),
    refetchInterval: 30_000,
  });

  const floor = useQuery({
    queryKey: ['floor', venueId, date, 'timeline'],
    enabled: Boolean(venueId && date),
    queryFn: () =>
      api.get<{ tables: Array<{ id: string; reservation: { id: string } | null }> }>(
        '/api/v1/floor',
        { venue_id: venueId, date },
      ),
  });

  /**
   * Which table each booking sits on.
   *
   * The floor endpoint answers "who is on this table at an instant", so a
   * booking that has already finished is not in it. Bookings with no known
   * table are grouped into an unassigned row rather than dropped — a booking
   * you cannot see is worse than one you cannot place.
   */
  const tableByReservation = useMemo(() => {
    const map = new Map<string, string>();
    for (const table of floor.data?.tables ?? []) {
      if (table.reservation) map.set(table.reservation.id, table.id);
    }
    return map;
  }, [floor.data]);

  const hourMarks = useMemo(() => {
    const marks: number[] = [];
    for (let minute = WINDOW_START_MINUTES; minute <= WINDOW_END_MINUTES; minute += 120) {
      marks.push(minute);
    }
    return marks;
  }, []);

  function progressFor(iso: string): number {
    if (!venue) return 0;
    const clock = venueClock(iso, venue);
    let minutes = clockToMinutes(clock);
    // A 01:30 suhoor booking is 25:30 in this window, not 01:30.
    if (minutes < WINDOW_START_MINUTES) minutes += 24 * 60;
    return (minutes - WINDOW_START_MINUTES) / WINDOW_SPAN;
  }

  if (!venue) return <p className="text-sm text-slate-500">{t('app.loading')}</p>;

  const rows = layout.data?.tables ?? [];

  return (
    <div className="flex flex-col gap-4">
      <header className="flex flex-col gap-1">
        <h1 className="text-2xl font-semibold">{t('timeline.title')}</h1>
        <ServiceDatePicker value={date} onChange={setServiceDate} venue={venue} />
      </header>

      <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
        <div className="min-w-[900px]">
          {/* The axis. Ticks are positioned with inlineStartPercent, so the
              earliest hour sits at the inline START - the right-hand edge in
              Arabic - and the evening reads in the same direction as the text. */}
          <div className="relative h-8 border-b border-slate-200 bg-slate-50">
            {hourMarks.map((minute) => (
              <span
                key={minute}
                className="absolute top-1 -translate-x-1/2 font-mono text-[11px] text-slate-500"
                style={{ insetInlineStart: inlineStartPercent(
                  (minute - WINDOW_START_MINUTES) / WINDOW_SPAN,
                  dir,
                ) }}
                data-numeric
              >
                {minutesToClock(minute, true)}
              </span>
            ))}
          </div>

          {rows.map((table) => {
            const onThisTable = (bookings.data?.reservations ?? []).filter(
              (booking) => tableByReservation.get(booking.id) === table.id,
            );

            return (
              <div
                key={table.id}
                className="relative flex h-12 items-center border-b border-slate-100"
              >
                <div className="sticky start-0 z-10 w-24 shrink-0 border-e border-slate-200 bg-white px-2 py-1">
                  <span className="font-mono text-sm">{table.label}</span>
                  <span className="ms-2 text-[10px] text-slate-400" data-numeric>
                    {`${table.min_party}-${table.max_party}`}
                  </span>
                </div>

                <div className="relative h-full flex-1">
                  {onThisTable.map((booking) => {
                    const start = progressFor(booking.starts_at);
                    const end = progressFor(booking.ends_at);
                    const width = Math.max(0.02, Math.abs(end - start));

                    return (
                      <button
                        key={booking.id}
                        type="button"
                        className="absolute top-1.5 h-9 overflow-hidden rounded border border-brand-teal bg-brand-teal-light px-2 text-start text-[11px] leading-tight hover:bg-brand-teal hover:text-white"
                        style={{
                          insetInlineStart: inlineStartPercent(
                            dir === 'rtl' ? start + width : start,
                            dir,
                          ),
                          width: `${width * 100}%`,
                        }}
                        title={`${booking.booking_ref} · ${booking.party_size}`}
                      >
                        <span className="block truncate font-medium">
                          {booking.guest?.first_name ?? t('common.guest')}
                        </span>
                        <span className="block truncate" data-numeric>
                          {`${formatTime(booking.starts_at, venue, i18n.language)} · ${booking.party_size}`}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}

          {rows.length === 0 ? (
            <p className="p-6 text-center text-sm text-slate-500">{t('timeline.noBookings')}</p>
          ) : null}
        </div>
      </div>

      <p className="text-xs text-slate-500">
        {t('reservations.title')}
        {': '}
        <span data-numeric>{bookings.data?.reservations.length ?? 0}</span>
      </p>

      <ul className="flex flex-wrap gap-2">
        {(bookings.data?.reservations ?? []).slice(0, 12).map((booking) => (
          <li key={booking.id}>
            <StatusPill status={booking.status} />
          </li>
        ))}
      </ul>
    </div>
  );
}
